import { useState, useRef, useEffect } from 'react'
import { ArrowLeft, CircleNotch, XCircle } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import api from '../../api/api'
import { APP_COLORS } from '../../helpers/constants'

import { useOfflineMode } from './hooks/useOfflineMode'

const SLOW_CONNECTION_TIMEOUT = 40000 // 40 seconds

const EnterCode = ({ onBack, onSuccess, onShowWeeklyCode }) => {
  const { t } = useTranslation(['package-room', 'common'])
  const [accessCode, setAccessCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const { isConnectionIssue, weeklyCode, setHasBadConnection } = useOfflineMode()

  const requestTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current)
      }
    }
  }, [])

  const handleAccessCodeChange = (e) => {
    setAccessCode(e.target.value)
    setErrorMessage('')
  }

  // Note: We don't verify the code offline for now. It's a hotfix. For now we check only the length.
  const verifyCodeOffline = (code) => {
    return weeklyCode && code.trim().length === 4
  }

  const handleAccessCodeSubmit = async () => {
    if (!accessCode.trim()) {
      return
    }

    const code = accessCode.trim()

    if (isConnectionIssue) {
      if (verifyCodeOffline(code)) {
        onShowWeeklyCode()
      } else {
        setErrorMessage(t('package-room:enterCode.errorVerifyFailed'))
      }
      return
    }

    try {
      setIsVerifying(true)
      setErrorMessage('')

      requestTimeoutRef.current = setTimeout(() => {
        setHasBadConnection(true)
        setIsVerifying(false)

        if (verifyCodeOffline(code)) {
          onShowWeeklyCode()
        } else {
          setErrorMessage(t('package-room:enterCode.errorConnectionFailed'))
        }
      }, SLOW_CONNECTION_TIMEOUT)

      const { data } = await api.verifyPackageRoomAccessCode(code)
      clearTimeout(requestTimeoutRef.current)
      setIsVerifying(false)
      if (data.doorUnlocked) {
        onSuccess()
      } else {
        onShowWeeklyCode()
      }
    } catch (error) {
      clearTimeout(requestTimeoutRef.current)
      setIsVerifying(false)
      // Note: This is as hotfix and temporary handling (especially for Arrive Hollywood building)
      if (!!error?.response?.status && error.response.status === 400) {
        setErrorMessage(t('package-room:enterCode.errorInvalidCode'))
      } else {
        onShowWeeklyCode()
      }
    }
  }

  if (isVerifying) {
    return (
      <div className="package-room__content package-room__content--full">
        <div className="package-room__access-container package-room__access-container--unlocked">
          <div className="package-room__icon package-room__icon--loading">
            <CircleNotch
              size={64}
              color={APP_COLORS.black300}
              weight="regular"
              style={{ animation: 'spin 1s linear infinite' }}
            />
          </div>

          <h2 className="package-room__title package-room__title--unlocked">
            {t('package-room:enterCode.connecting')}
          </h2>

          <div className="package-room__unlocked-message">
            <p>{t('package-room:enterCode.connectingMessage')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="package-room__content package-room__content--full">
      <button className="package-room__back" onClick={onBack}>
        <ArrowLeft size={24} />
        {t('common:actions.back')}
      </button>
      <h2 className="package-room__title">{t('package-room:enterCode.title')}</h2>
      <div className="package-room__custom-input-container">
        <input
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          value={accessCode}
          onChange={handleAccessCodeChange}
          placeholder={t('package-room:enterCode.placeholder')}
          className="package-room__custom-input"
          autoFocus
        />
        {errorMessage && (
          <div className="package-room__error-message">
            <XCircle size={20} weight="fill" />
            {errorMessage}
          </div>
        )}
        <button
          className="package-room__submit-button"
          onClick={handleAccessCodeSubmit}
          disabled={!accessCode.trim() || isVerifying}
        >
          {isVerifying ? t('package-room:enterCode.verifying') : t('package-room:enterCode.submit')}
        </button>
      </div>
    </div>
  )
}

EnterCode.propTypes = {
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onShowWeeklyCode: PropTypes.func.isRequired,
}

export default EnterCode
